import React, { useCallback, useState, useRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { DateInput } from 'components/atoms/DateInput';

interface SingleDatePickerFormWrapperProps {
  date: Date | null;
  initialVisibleMonth: () => Date;
  isOutsideRange: (date: Date) => boolean;
  onChange: (date: Date) => void;
  onNextMonthClick: (date: Date) => void;
  onPrevMonthClick: (date: Date) => void;
  isCalendarLoading?: boolean;
}

const HostExperienceSingleDatePicker = forwardRef<
  HTMLDivElement,
  SingleDatePickerFormWrapperProps
>(
  (
    {
      date,
      initialVisibleMonth,
      isOutsideRange,
      onChange,
      onNextMonthClick,
      onPrevMonthClick,
      isCalendarLoading,
    },
    forwardedRef,
  ) => {
    const { t: translate } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dateInputRef = useRef<HTMLDivElement>(null);

    // Use the forwarded ref if provided, otherwise use local ref
    const effectiveRef =
      (forwardedRef as React.RefObject<HTMLDivElement>) || dateInputRef;

    const getActiveDate = useCallback(
      (date: Date | null) => {
        if (!date || (!isCalendarLoading && isOutsideRange(date))) {
          return undefined;
        }
        return date;
      },
      [isOutsideRange, isCalendarLoading],
    );

    const handleDateSelect = useCallback(
      (selectedDate: Date) => {
        onChange(selectedDate);
        setIsOpen(false);
      },
      [onChange],
    );

    return (
      <DayPickerPopover
        isLoading={isCalendarLoading}
        mode="single"
        required
        animate
        defaultMonth={initialVisibleMonth()}
        selected={getActiveDate(date)}
        onSelect={handleDateSelect}
        modifiers={{
          disabled: isOutsideRange,
        }}
        onNextClick={onNextMonthClick}
        onPrevClick={onPrevMonthClick}
        open={isOpen}
        onOpenChange={setIsOpen}
        triggerRef={effectiveRef}
        asChild
        trigger={
          <DateInput
            ref={effectiveRef}
            date={getActiveDate(date) || new Date()}
            id="start-date-input"
            dateFormat="MMM d"
            placeholder={translate('date')}
          />
        }
        sideOffset={10}
        portalled
        footer={null}
      />
    );
  },
);

HostExperienceSingleDatePicker.displayName = 'HostExperienceSingleDatePicker';

export default HostExperienceSingleDatePicker;
